/** @format */
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CourseUnitResultItem, OtmId, SearchParameters, SearchResult, SearchValidity } from 'common-typescript/types';
import { isEqual, omit } from 'lodash-es';
import { combineLatest, distinctUntilChanged, finalize, Observable, of, ReplaySubject, shareReplay, Subject, switchMap, tap } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AuthService, singleConcurrentSearchWithThrottle } from 'sis-common';
import {
    AppErrorHandler,
    ComboboxOption,
    CourseUnitEntityService,
    CurriculumPeriodEntityService,
    Option,
    SearchService,
    silentErrorHandler,
} from 'sis-components';
import { CourseCartEntityService } from '../../common/service/course-cart-entity.service';
import { CourseUnitSearchFilters } from '../search.types';
import { getStudyPeriodInfoLoader } from '../search.utils';

const RESULTS_PER_PAGE = 20;

@Component({
    selector: 'app-search-main',
    templateUrl: './search-main.component.html',
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: SearchService,
            useFactory: () =>
                new SearchService<CourseUnitSearchFilters>({
                    storageKey: 'searchParameters.student.search.main',
                    universityOrgIdEnabled: true,
                    defaultOptions: {
                        start: 0,
                        limit: RESULTS_PER_PAGE,
                    },
                }),
        },
    ],
    standalone: false,
})
export class SearchMainComponent implements OnInit, OnDestroy {
    currentPage = 1;

    courseCartCourseUnitIds: OtmId[] = [];

    destroyed$ = new Subject<void>();

    /**
     * Indicates whether a search request is pending.
     */
    searching = false;

    searchSortOptions: Option[] = [
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.MATCH'), value: null },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.NAME_ASC'), value: 'name' },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.NAME_DESC'), value: '-name' },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.CREDITS_ASC'), value: 'credits' },
        { label: this.translate.instant('SEARCH.SORT_SELECTOR.SORT_METHOD_OPTIONS.CREDITS_DESC'), value: '-credits' },
    ];

    readonly enrolmentPeriodOptions: ComboboxOption[] = [
        { label: this.translate.instant('SEARCH_PAGE_ENROLMENT_ONGOING'), value: 'ONGOING' },
        { label: this.translate.instant('SEARCH_PAGE_ENROLMENT_FUTURE'), value: 'FUTURE' },
    ];

    private readonly validParameters$ = new ReplaySubject<SearchParameters<CourseUnitSearchFilters>>();
    readonly searchResult$ = this.validParameters$.pipe(
        singleConcurrentSearchWithThrottle(parameters => this.executeSearch(parameters)),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly curriculumPeriodIds$: Observable<OtmId[]> = this.searchService.searchParameters$.pipe(
        map(params => params.filters?.curriculumPeriodId ?? []),
        map(ids => (Array.isArray(ids) ? ids : [ids]).filter(Boolean)),
        distinctUntilChanged(isEqual),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly curriculumPeriods$ = this.searchService.universityOrgId$.pipe(
        switchMap(id => this.curriculumPeriodService.getByUniversityOrgId(id).pipe(catchError(() => of([])))),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly studyPeriodInfos$ = getStudyPeriodInfoLoader(this.searchService.universityOrgId$);

    readonly viewData$ = combineLatest({
        curriculumPeriods: this.curriculumPeriods$,
        studyPeriodInfos: this.studyPeriodInfos$,
    });

    constructor(
        private authService: AuthService,
        private courseCartEntityService: CourseCartEntityService,
        private translate: TranslateService,
        private appErrorHandler: AppErrorHandler,
        private curriculumPeriodService: CurriculumPeriodEntityService,
        private courseUnitService: CourseUnitEntityService,
        protected searchService: SearchService<CourseUnitSearchFilters>,
    ) {}

    ngOnInit(): void {
        if (this.isLoggedIn()) {
            this.courseCartEntityService
                .getCourseCart(true)
                .pipe(takeUntil(this.destroyed$), this.appErrorHandler.defaultErrorHandler())
                .subscribe(ids => (this.courseCartCourseUnitIds = ids));
        }
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    onSearchParameterChange(parameters: SearchParameters<CourseUnitSearchFilters>) {
        this.validParameters$.next(parameters);
    }

    isLoggedIn(): boolean {
        return this.authService.loggedIn();
    }

    sort(sortValue: string) {
        this.searchService.sort(sortValue);
    }

    addCourseUnitToCourseCart(courseUnitId: string) {
        this.courseCartEntityService
            .addCurToCourseCart(courseUnitId)
            .pipe(takeUntil(this.destroyed$), this.appErrorHandler.defaultErrorHandler())
            .subscribe();
    }

    removeCourseUnitFromCourseCart(courseUnitId: string) {
        this.courseCartEntityService
            .deleteCurFromCourseCart(courseUnitId)
            .pipe(takeUntil(this.destroyed$), this.appErrorHandler.defaultErrorHandler())
            .subscribe();
    }

    onPaginationChange(newPage: number) {
        this.currentPage = newPage + 1; // Adjust for 0-based index
        this.searchService.patchOptions({ start: newPage * this.resultsPerPage });
        document.getElementById('results-show-guide')?.focus();
    }

    get resultsPerPage() {
        return RESULTS_PER_PAGE;
    }

    readonly totalPages$ = this.searchResult$.pipe(
        map(searchResult => {
            const total = searchResult?.total ?? 0;
            const pages = Math.ceil(total / this.resultsPerPage);
            return pages > 0 ? pages : 1;
        }),
    );

    private executeSearch({ filters, options }: SearchParameters<CourseUnitSearchFilters>): Observable<SearchResult<CourseUnitResultItem>> {
        this.searching = true;
        let enrolmentPeriodValidity: SearchValidity;
        if (filters.enrolmentPeriodValidity?.includes('ONGOING') && filters.enrolmentPeriodValidity?.includes('FUTURE')) {
            enrolmentPeriodValidity = 'ONGOING_AND_FUTURE';
        } else {
            enrolmentPeriodValidity = filters.enrolmentPeriodValidity?.[0];
        }
        return this.courseUnitService
            .searchActive({
                ...options,
                ...omit(filters, ['attainmentLanguage', 'studyLevel', 'teachingLanguage', 'studyField']),
                assessmentItemType: filters.assessmentItemType,
                codeUrn: [...(filters.attainmentLanguage ?? []), ...(filters.studyLevel ?? []), ...(filters.studyField ?? [])],
                curCodeUrn: filters.teachingLanguage,
                sort: options.sort?.join(','),
                enrolmentPeriodValidity,
                validity: 'ONGOING_AND_FUTURE',
            })
            .pipe(
                tap(results => (this.currentPage = Math.floor((results?.start ?? 0) / RESULTS_PER_PAGE) + 1)),
                finalize(() => (this.searching = false)),
                silentErrorHandler,
            ) as Observable<SearchResult<CourseUnitResultItem>>;
    }
}
